<template>
    <div class="apperty-integration p-3">
        <!-- Token Section -->
        <div class="detail-section mb-4">
            <h6 class="section-title">
                <i class="fas fa-key text-primary me-2"></i>
                Token de Apperty
            </h6>
            <div v-if="loadingToken" class="text-center py-2">
                <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
                <span class="ms-2 small text-muted">Cargando token...</span>
            </div>
            <div v-else class="row align-items-end g-2">
                <div class="col-md-8 col-12">
                    <label class="form-label fw-semibold small">Token de acceso</label>
                    <input type="text" class="form-control" v-model="token"
                        placeholder="Pega aquí el token de Apperty" :disabled="savingToken" />
                </div>
                <div class="col-md-4 col-12">
                    <soft-button variant="gradient" size="sm" class="w-100"
                        @click="saveToken" :disabled="!token || savingToken">
                        <span v-if="savingToken" class="spinner-border spinner-border-sm me-1" role="status"></span>
                        <i v-else class="fas fa-save me-1"></i>
                        Guardar Token
                    </soft-button>
                </div>
            </div>
        </div>

        <!-- Migration Section -->
        <div class="detail-section mb-3">
            <h6 class="section-title">
                <i class="fas fa-file-import text-primary me-2"></i>
                Migrar Propiedades
            </h6>
            <p class="text-muted small mb-3">
                Importa las propiedades desde Apperty hacia CRM RED. Las propiedades duplicadas seran omitidas.
            </p>
            <soft-button variant="gradient" size="sm" @click="importProperties"
                :disabled="!tokenSaved || !token || importing">
                <span v-if="importing" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <i v-else class="fas fa-cloud-download-alt me-1"></i>
                Migrar Propiedades a CRM RED
            </soft-button>
        </div>

        <!-- Result Alert -->
        <soft-alert v-if="alertState" class="mt-3">
            {{ alertMessage }}
        </soft-alert>

        <!-- Error Alert -->
        <div v-if="errorMessage" class="alert alert-danger mt-3 small" role="alert">
            <i class="fas fa-exclamation-triangle me-1"></i>
            {{ errorMessage }}
        </div>
    </div>
</template>

<script lang="js">
import { ref, watch } from 'vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftAlert from '@/components/SoftAlert.vue';
import apiClient from '@/libs/axios';
import { apiUrl } from '@/constans';

export default {
    name: 'AppertyIntegration',
    components: { SoftButton, SoftAlert },
    props: {
        userId: {
            type: [Number, String],
            required: true
        },
        userEmail: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const token = ref('');
        const tokenSaved = ref(false);
        const savingToken = ref(false);
        const loadingToken = ref(false);
        const importing = ref(false);
        const alertState = ref(false);
        const alertMessage = ref('');
        const errorMessage = ref('');

        const clearMessages = () => {
            errorMessage.value = '';
            alertState.value = false;
            alertMessage.value = '';
        };

        const showSuccess = (message) => {
            clearMessages();
            alertState.value = true;
            alertMessage.value = message;
            setTimeout(() => {
                alertState.value = false;
                alertMessage.value = '';
            }, 5000);
        };

        const showError = (message) => {
            clearMessages();
            errorMessage.value = message;
            setTimeout(() => {
                errorMessage.value = '';
            }, 7000);
        };

        const getToken = async () => {
            loadingToken.value = true;
            token.value = '';
            tokenSaved.value = false;
            try {
                const response = await apiClient.get(`${apiUrl}/v2/apperty/get-token`, {
                    params: { user_id: props.userId }
                });
                const data = response.data;
                const savedToken = data.data?.token || data.token;
                if (savedToken) {
                    token.value = savedToken;
                    tokenSaved.value = true;
                }
            } catch (error) {
                console.error('Error obteniendo token de Apperty:', error);
            } finally {
                loadingToken.value = false;
            }
        };

        const saveToken = async () => {
            if (!token.value) return;
            savingToken.value = true;
            clearMessages();
            try {
                await apiClient.post(`${apiUrl}/v2/apperty/save-token`, {
                    token: token.value,
                    user_id: props.userId
                });
                tokenSaved.value = true;
                showSuccess('Token guardado correctamente.');
            } catch (error) {
                const rd = error.response?.data;
                const msg = rd?.data || rd?.message || rd?.errorMessage || 'Error al guardar el token. Verifica que sea valido.';
                showError(typeof msg === 'string' ? msg : 'Error al guardar el token. Verifica que sea valido.');
            } finally {
                savingToken.value = false;
            }
        };

        const importProperties = async () => {
            if (!tokenSaved.value || !token.value) return;
            importing.value = true;
            clearMessages();
            try {
                const response = await apiClient.post(`${apiUrl}/v2/apperty/import-properties`, {
                    token: token.value,
                    email: props.userEmail
                });
                const data = response.data;
                if (data.success === false) {
                    const msg = data.data || data.message || 'Error al importar propiedades desde Apperty.';
                    showError(typeof msg === 'string' ? msg : 'Error al importar propiedades desde Apperty.');
                    return;
                }
                const imported = data.imported || 0;
                const skipped = data.skipped || 0;
                showSuccess(`Se importaron ${imported} propiedades. ${skipped} ya existian y fueron omitidas.`);
            } catch (error) {
                const rd = error.response?.data;
                const msg = rd?.data || rd?.message || rd?.errorMessage || 'Error al importar propiedades. Verifica tu token y conexion con Apperty.';
                showError(typeof msg === 'string' ? msg : 'Error al importar propiedades. Verifica tu token y conexion con Apperty.');
            } finally {
                importing.value = false;
            }
        };

        // Cargar token al montar y cuando cambie el userId
        watch(() => props.userId, () => {
            clearMessages();
            getToken();
        }, { immediate: true });

        return {
            token,
            tokenSaved,
            savingToken,
            loadingToken,
            importing,
            alertState,
            alertMessage,
            errorMessage,
            saveToken,
            importProperties
        };
    }
};
</script>

<style lang="scss" scoped>
.apperty-integration {
    .section-title {
        color: #344767;
        font-weight: 600;
        font-size: 0.9rem;
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
    }

    .detail-section {
        padding: 1rem;
        background: #f8f9fa;
        border-radius: 0.5rem;
        border: 1px solid #e9ecef;
    }

    .form-control:focus {
        border-color: #ef9a9a;
        box-shadow: 0 0 0 2px rgba(229, 57, 53, 0.12);
    }
}
</style>
